import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, Menu, MenuItem, Button, Portal, } from '@mui/material';
import { CloudUpload, Edit, Trash2, Send } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSocialYtTrackerLists, manageSocialYtTracker, importSocialYtExcel, sendSocialYtForApproval, setSocialYtFilters } from '../../redux/socialYtTrackerSlice';
import { selectEntityType } from '../../redux/authSlice';
import { useAccountOverview } from '../../context/AccountOverviewContext';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import TableToolbar from '../../common_components/TableToolbar';
import useToast from '../../common_components/Toast/useToast';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import NotesModal from '../../common_components/NotesModal';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import UploadSocialYtDrawer from './UploadSocialYtDrawer';
import { useNavigate } from 'react-router-dom';
const fmtDate = (dateStr) => {
    if (!dateStr)
        return '—';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: '2-digit', year: 'numeric'
        });
    }
    catch {
        return dateStr;
    }
};
export default function SocialYtTrackerList({ trackerType, clientName = '' }) {
    const dispatch = useDispatch();
    const toast = useToast();
    const navigate = useNavigate();
    const { selectedClientId } = useAccountOverview();
    const { lists, loading } = useSelector((state) => state.socialYtTracker);
    const entityType = useSelector(selectEntityType);
    const isClient = entityType === 'client';
    const permissions = useSelector((state) => state.auth.permissions);
    const affiliateActions = permissions?.affiliate_account_performance?.actions || [];
    const hasManageTrackersAccess = affiliateActions.includes('Manage Trackers');
    const hasClientApprovalAccess = affiliateActions.includes('Client Approval Access');
    // Pagination & Search
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchText, setSearchText] = useState('');
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    // Menu state
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [activeMenuTracker, setActiveMenuTracker] = useState(null);
    // Modal/Drawer state
    const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [notesTracker, setNotesTracker] = useState(null);
    const [notesValue, setNotesValue] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    // Fetch on mount & filter changes
    useEffect(() => {
        dispatch(setSocialYtFilters({
            selectedClientId: selectedClientId || '',
            selectedTrackerType: trackerType,
            sortBy,
            sortOrder
        }));
        dispatch(fetchSocialYtTrackerLists());
    }, [selectedClientId, trackerType, sortBy, sortOrder, dispatch]);
    // Filter by this tracker type
    const typedLists = useMemo(() => lists.filter(l => l.tracker_type === trackerType), [lists, trackerType]);
    const filteredLists = useMemo(() => {
        if (!searchText.trim())
            return typedLists;
        const q = searchText.toLowerCase();
        return typedLists.filter(item => item.name?.toLowerCase().includes(q) ||
            item.created_by?.name?.toLowerCase().includes(q));
    }, [typedLists, searchText]);
    const totalPages = Math.ceil(filteredLists.length / itemsPerPage);
    const paginatedLists = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredLists.slice(start, start + itemsPerPage);
    }, [filteredLists, currentPage]);
    // Menu handlers
    const handleMenuOpen = (e, tracker) => {
        setMenuAnchor(e.currentTarget);
        setActiveMenuTracker(tracker);
    };
    const handleMenuClose = () => setMenuAnchor(null);
    // Edit tracker — navigate to edit detail page
    const handleEditClick = () => {
        handleMenuClose();
        if (!activeMenuTracker)
            return;
        const fromTab = activeMenuTracker.tracker_type === 'youtube' ? 'youtube_tracker' : 'social_tracker';
        navigate(`/social-yt-tracker?id=${activeMenuTracker.id}&type=${activeMenuTracker.tracker_type}&from=${fromTab}`, {
            state: {
                trackerId: activeMenuTracker.id,
                trackerType: activeMenuTracker.tracker_type,
                trackerListName: activeMenuTracker.name,
                clientName,
                from: fromTab,
            }
        });
    };
    // Notes
    const handleNotesClick = (row) => {
        setNotesTracker(row);
        setNotesValue(row.notes || '');
        setNotesModalOpen(true);
    };
    const handleNotesSave = async (newNotes) => {
        if (!notesTracker)
            return;
        try {
            setActionLoading(true);
            await dispatch(manageSocialYtTracker({
                action: 'update_tracker',
                tracker_id: notesTracker.id,
                updates: { notes: newNotes.trim() }
            })).unwrap();
            toast.success('Notes updated successfully!');
            setNotesModalOpen(false);
            dispatch(fetchSocialYtTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to update notes.');
            throw err;
        }
        finally {
            setActionLoading(false);
        }
    };
    // Send for Approval
    const handleSendApproval = async () => {
        if (!activeMenuTracker)
            return;
        handleMenuClose();
        try {
            setActionLoading(true);
            await dispatch(sendSocialYtForApproval({ tracker_id: activeMenuTracker.id })).unwrap();
            toast.success(activeMenuTracker.hasApprovalSent
                ? 'Tracker resent for approval successfully.'
                : 'Tracker sent for approval successfully.');
            dispatch(fetchSocialYtTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to send for approval.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // Delete tracker
    const handleDeleteClick = () => {
        handleMenuClose();
        setDeleteModalOpen(true);
    };
    const confirmDeleteTracker = async () => {
        if (!activeMenuTracker)
            return;
        try {
            setActionLoading(true);
            await dispatch(manageSocialYtTracker({
                action: 'delete_tracker',
                tracker_id: activeMenuTracker.id
            })).unwrap();
            toast.success('Tracker deleted successfully.');
            setDeleteModalOpen(false);
            dispatch(fetchSocialYtTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to delete tracker.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // Upload
    const handleUploadSubmit = async (data) => {
        try {
            setActionLoading(true);
            const formData = new FormData();
            formData.append('file', data.file);
            formData.append('tracker_type', trackerType);
            formData.append('creation_date', data.creationDate);
            if (selectedClientId)
                formData.append('client_id', selectedClientId);
            await dispatch(importSocialYtExcel(formData)).unwrap();
            toast.success('Tracker imported successfully.');
            setUploadDrawerOpen(false);
            dispatch(fetchSocialYtTrackerLists());
        }
        catch (err) {
            toast.error(err || 'Failed to import tracker.');
        }
        finally {
            setActionLoading(false);
        }
    };
    // View details
    const handleViewDetails = (tracker) => {
        const fromTab = trackerType === 'social' ? 'social_tracker' : 'youtube_tracker';
        navigate(`/social-yt-tracker/${tracker.id}?id=${tracker.id}&type=${tracker.tracker_type}&from=${fromTab}`, {
            state: {
                trackerId: tracker.id,
                trackerType: tracker.tracker_type,
                trackerListName: tracker.name,
                clientName,
                from: fromTab
            }
        });
    };
    const handleSortChange = (columnId, direction) => {
        setSortBy(columnId);
        setSortOrder(direction);
    };
    // Columns
    const columns = [
        { id: 'name', label: 'Tracker Name', width: '18%', sortable: true, align: 'left' },
        { id: 'created_by', label: 'Created By', sortable: true, align: 'left' },
        { id: 'creation_date', label: 'Creation Date', sortable: true, align: 'left' },
        { id: 'status', label: 'Status', sortable: true, align: 'left' },
        { id: 'contacts', label: 'Contacts', sortable: true, align: 'left' },
        { id: 'action', label: 'Action', align: 'left' }
    ];
    const renderStatusPill = (status) => {
        let bgcolor = '#F2F4F7';
        let color = '#344054';
        if (status === 'Completed') {
            bgcolor = '#ECFDF3';
            color = '#027A48';
        }
        else if (status === 'In-Progress') {
            bgcolor = '#F9F5FF';
            color = '#6941C6';
        }
        return (_jsx(Box, { sx: { display: 'inline-flex', px: '8px', py: '2px', borderRadius: '16px', bgcolor, color, fontSize: '12px', fontWeight: 600 }, children: status }));
    };
    const renderContactsValue = (contacts) => {
        const total = contacts?.total || 0;
        const synced = contacts?.synced || 0;
        const duplicate = contacts?.duplicate || 0;
        return (_jsxs(Box, { sx: { display: 'inline-flex', alignItems: 'center', px: '10px', py: '4px', borderRadius: '16px', bgcolor: '#F9F5FF', color: '#7F56D9', fontSize: '12px', fontWeight: 600 }, children: [_jsx(HoverTooltip, { title: "All contacts - (contacts without primary email + duplicate contacts)", children: _jsx("span", { style: { cursor: 'help' }, children: total }) }), _jsx("span", { style: { margin: '0 2px' }, children: "/" }), _jsx(HoverTooltip, { title: "Total contacts synced to HubSpot", children: _jsx("span", { style: { cursor: 'help' }, children: synced }) }), _jsx("span", { style: { marginLeft: '6px' } }), _jsx(HoverTooltip, { title: "Total no. of contacts with repeating primary email.", children: _jsxs("span", { style: { cursor: 'help' }, children: ["(", duplicate, ")"] }) })] }));
    };
    const renderCell = (row, column) => {
        switch (column.id) {
            case 'name': {
                const name = row.name || '';
                const isTruncated = name.length > 25;
                const displayName = isTruncated ? `${name.substring(0, 25)}...` : name;
                const cell = (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', fontWeight: 600, color: '#101828', cursor: isTruncated ? 'help' : 'default' }, children: displayName }));
                return isTruncated ? (_jsx(HoverTooltip, { title: name, children: _jsx(Box, { sx: { display: 'inline-block' }, children: cell }) })) : cell;
            }
            case 'created_by':
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#475467' }, children: row.created_by?.name || 'System' }));
            case 'creation_date':
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#475467' }, children: fmtDate(row.creation_date) }));
            case 'status':
                return renderStatusPill(row.status);
            case 'contacts':
                return renderContactsValue(row.contacts);
            case 'action':
                return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '8px' }, children: [_jsx(IconButton, { size: "small", onClick: () => handleViewDetails(row), sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "View Contacts", children: _jsx("img", { src: "/assets/eye.svg", alt: "View", style: { width: 16, height: 16 } }) }), _jsxs(Box, { sx: { position: 'relative', display: 'inline-flex' }, children: [_jsx(IconButton, { size: "small", onClick: () => handleNotesClick(row), sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "Tracker Notes", children: _jsx("img", { src: "/assets/book.svg", alt: "Note", style: { width: 16, height: 16 } }) }), row.notes && (_jsx(Box, { sx: { position: 'absolute', top: '4px', right: '4px', width: '6px', height: '6px', borderRadius: '50%', bgcolor: '#F04438', border: '1px solid #fff' } }))] }), (hasManageTrackersAccess || (!isClient && hasClientApprovalAccess)) && (_jsx(IconButton, { size: "small", onClick: (e) => handleMenuOpen(e, row), sx: { p: '6px', borderRadius: '6px', '&:hover': { bgcolor: '#F2F4F7' } }, title: "Actions Menu", children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [_jsx("circle", { cx: "8", cy: "4", r: "1.5", fill: "#667085" }), _jsx("circle", { cx: "8", cy: "8", r: "1.5", fill: "#667085" }), _jsx("circle", { cx: "8", cy: "12", r: "1.5", fill: "#667085" })] }) }))] }));
            default:
                return null;
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '20px' }, children: _jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #D0D5DD', overflow: 'hidden' }, className: "table-scroll-container", children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'end', borderBottom: '1px solid #EAECF0', flexWrap: 'wrap', gap: '12px', px: 1 }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }, children: [_jsx(TableToolbar, { title: "", showSearch: true, searchValue: searchText, onSearchChange: setSearchText, showRows: true, showExpand: true, showFullScreen: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), searchPlaceholder: "Search trackers..." }), !isClient && hasManageTrackersAccess && (_jsx(Button, { variant: "outlined", startIcon: _jsx(CloudUpload, { size: 16 }), onClick: () => setUploadDrawerOpen(true), className: "all-Font-Sizes", sx: {
                                            color: '#344054', borderColor: '#D0D5DD', textTransform: 'none',
                                            height: '40px', borderRadius: '8px', fontWeight: 600,
                                            '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' }
                                        }, children: "Upload Tracker" }))] }) }), _jsx(CommonTable, { columns: columns, rows: paginatedLists, renderCell: renderCell, isLoading: loading || actionLoading, isFirstLoad: loading && lists.length === 0, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (colId, dir) => handleSortChange(colId, dir || 'desc'), expandTrigger: "none", stickyHeader: true, maxHeight: "calc(100vh - 360px)", hiddenColumns: hiddenColumns, onColumnVisibilityChange: (newHidden) => setHiddenColumns(newHidden), columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnVisibilityConfig: {
                                enable: true,
                                columns: ['name', 'created_by', 'creation_date', 'contacts', 'status', 'action'],
                                enableReorder: true
                            } }), _jsx(Box, { sx: { p: '12px 16px', borderTop: '1px solid #EAECF0' }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, totalEntries: filteredLists.length, itemsPerPage: itemsPerPage, onPageChange: setCurrentPage, showEntriesMessage: true }) })] }) }), Boolean(menuAnchor) && (_jsx(Portal, { children: _jsx(Box, { onClick: handleMenuClose, sx: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.15)', zIndex: 1400 } }) })), _jsxs(Menu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: handleMenuClose, disableScrollLock: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, sx: {
                    zIndex: 1500,
                    '& .MuiPaper-root': {
                        borderRadius: '8px',
                        boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
                        border: '1px solid #EAECF0', mt: 0.5, minWidth: '180px'
                    }
                }, children: [hasManageTrackersAccess && (_jsxs(MenuItem, { onClick: handleEditClick, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(Edit, { size: 16 }), "Edit Tracker"] })), hasClientApprovalAccess && (_jsxs(MenuItem, { onClick: handleSendApproval, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#344054' }, children: [_jsx(Send, { size: 16 }), activeMenuTracker?.hasApprovalSent ? 'Resend For Approval' : 'Send For Approval'] })), hasManageTrackersAccess && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { height: '1px', bgcolor: '#EAECF0', my: 0.5 } }), _jsxs(MenuItem, { onClick: handleDeleteClick, sx: { gap: '10px', py: '10px', fontSize: '14px', color: '#D92D20' }, children: [_jsx(Trash2, { size: 16 }), "Delete Tracker"] })] }))] }), _jsx(UploadSocialYtDrawer, { open: uploadDrawerOpen, onClose: () => { setUploadDrawerOpen(false); }, trackerType: trackerType, onSubmit: handleUploadSubmit, actionLoading: actionLoading }), _jsx(NotesModal, { open: notesModalOpen, onClose: () => setNotesModalOpen(false), initialNotes: notesValue, onSave: handleNotesSave, actionLoading: actionLoading }), _jsx(ConfirmActionModal, { open: deleteModalOpen, onClose: () => setDeleteModalOpen(false), onConfirm: confirmDeleteTracker, title: "Delete Tracker List?", description: `Are you sure you want to delete "${activeMenuTracker?.name}"? This will permanently delete the tracker and all its contacts from the database. This action cannot be undone.`, confirmText: "Delete List", cancelText: "Cancel", loading: actionLoading })] }));
}
